import { Injectable } from '@angular/core';
import { BaseEntity, BaseEntityResult, CompositeKey, Metadata, RunQuery, RunView, ValidationErrorInfo, type RunViewParams } from '@memberjunction/core';
import { IsOEPGuid, type OEPEntityName } from 'agu_oep_core';

/** A save/delete MJ refused; the message is OEPSaveMessage(LatestResult) (the rule text for validation refusals). */
export class OEPSaveError extends Error {
    constructor(message: string) {
        super(message);
        this.name = 'OEPSaveError';
    }
}

/** MJ shell entities an OEP view reads (OEP Staff reads both — tools/metadata/oep-permissions.js). */
export type OEPShellEntityName = 'MJ: Users' | 'MJ: User Roles';
export type OEPReadEntityName = OEPEntityName | OEPShellEntityName;
/** OEP entities plus MJ: User Roles (OEP Staff grants/revokes OEP Presenter in User management). */
export type OEPWriteEntityName = OEPEntityName | 'MJ: User Roles';
export interface OEPRowsQuery { EntityName: OEPReadEntityName; ExtraFilter: string; Fields: string[]; OrderBy?: string }
export interface OEPBatchOptions { readonly UnreadableAsEmpty?: boolean }

/**
 * The user-facing text of a refused save. A validation refusal leaves ValidationErrorInfo objects in
 * LatestResult.Errors, and MJ's CompleteMessage reads `err.message` (lower-case) from each, so it
 * JSON-stringifies them (core baseEntity.js:504-506) — the rule text is in `.Message`. T33b: a presenter
 * saw '{"Source":"SenderUserID","Message":"You can only save this as yourself.",…}'.
 */
export function OEPSaveMessage(result: BaseEntityResult | null | undefined): string {
    if (!result) return 'Save failed';
    // any[] in MJ's type: narrowed per element below.
    const errors: readonly unknown[] = result.Errors ?? [];
    const rules = errors.filter((e): e is ValidationErrorInfo => e instanceof ValidationErrorInfo).map((e) => e.Message).filter((m) => m.trim() !== '');
    if (rules.length > 0) return rules.join(' ');
    return result.CompleteMessage ?? 'Save failed';
}

/**
 * The read-failure message. MJ's RunDynamicView drops the provider's reason (docs/mj-upstream-defects.md MJ-020), so a
 * failed view can arrive with `ErrorMessage` null or empty; the user must never read "null".
 */
export function OEPLoadFailure(EntityName: string, ErrorMessage: string | null | undefined): string {
    const reason = ErrorMessage?.trim();
    return `Couldn't load ${EntityName}: ${reason ? reason : 'the server gave no reason. Try again.'}`;
}

/**
 * Thin client transport over MJ's client providers: RunView/RunViews for reads and
 * Metadata.GetEntityObject + BaseEntity.Save/Delete for writes, all through the
 * GraphQLDataProvider the Explorer shell configured. Every read is RLS-scoped by the server;
 * filters only narrow within what RLS allows. Client-side entity rules (agu_oep_core
 * LoadOEPEntityRules) run inside Save for immediate feedback; the server re-runs them.
 */
@Injectable()
export class OEPDataService {
    private readonly md = new Metadata();

    /** GUID guard for every value interpolated into an ExtraFilter. */
    static G(id: string): string {
        if (!IsOEPGuid(id)) throw new Error(`not a GUID: ${id}`);
        return id;
    }

    async Rows<T>(EntityName: OEPEntityName, ExtraFilter: string, Fields: string[], OrderBy?: string): Promise<T[]> {
        const r = await new RunView().RunView<T>({ EntityName, ExtraFilter, Fields, OrderBy });
        if (!r.Success) throw new Error(OEPLoadFailure(EntityName, r.ErrorMessage));
        return r.Results;
    }

    /**
     * Several reads in one round trip (RunView.RunViews). Rows are field bags: each query picks
     * its own Fields, so callers narrow per field. unknown: RunViews is one call over mixed shapes.
     *
     * `UnreadableAsEmpty` is for the cross-role context load: an entity none of the user's roles may
     * Read (EntityInfo.GetUserPermisions(...).CanRead, the same check the server's
     * CheckUserReadPermissions applies) yields no rows instead of failing the whole batch — a
     * presenter holds no Read on Session Nominators by design (T5 matrix). Such queries are not sent.
     */
    async Batch(queries: readonly OEPRowsQuery[], opts?: OEPBatchOptions): Promise<Record<string, unknown>[][]> {
        const send = queries.map((q) => opts?.UnreadableAsEmpty !== true || this.CanRead(q.EntityName));
        const sent = queries.filter((_q, i) => send[i]);
        const params: RunViewParams[] = sent.map((q) => ({ EntityName: q.EntityName, ExtraFilter: q.ExtraFilter, Fields: q.Fields, OrderBy: q.OrderBy }));
        const results = params.length > 0 ? await new RunView().RunViews<Record<string, unknown>>(params) : [];
        results.forEach((r, i) => {
            if (!r.Success) throw new Error(OEPLoadFailure(sent[i].EntityName, r.ErrorMessage));
        });
        let next = 0;
        return queries.map((_q, i) => (send[i] ? results[next++].Results : []));
    }

    /**
     * True when any of the current user's roles grants Read on the entity (MJ EntityInfo.GetUserPermisions).
     * An entity name metadata does not know is a programming error, not "unreadable": it throws, so a misspelled
     * name can never silently turn a participant's data into "skip this".
     */
    CanRead(EntityName: OEPReadEntityName): boolean {
        const entity = this.md.EntityByName(EntityName);
        if (entity === null || entity === undefined) throw new Error(`OEP: entity "${EntityName}" is not in MJ metadata.`);
        const user = this.md.CurrentUser;
        return user !== null && user !== undefined && entity.GetUserPermisions(user).CanRead;
    }

    /**
     * Saved MJ Queries by pinned ID in one round trip (RunQuery.RunQueries). The server checks
     * MJ: Query Permissions before running each. No CacheLocal: every load reads fresh counts.
     * unknown: a query row is its SQL's column bag, narrowed per field by the caller.
     */
    async Queries(queryIDs: readonly string[]): Promise<Record<string, unknown>[][]> {
        const results = await new RunQuery().RunQueries(queryIDs.map((QueryID) => ({ QueryID })));
        return queryIDs.map((id, i) => {
            const r = results[i];
            if (!r || !r.Success) throw new Error(`Couldn't load query ${id}: ${r?.ErrorMessage ?? 'no result'}`);
            const rows: Record<string, unknown>[] = r.Results;
            return rows;
        });
    }

    async SaveNew<T extends BaseEntity>(entity: OEPWriteEntityName, init: (e: T) => void): Promise<T> {
        const e = await this.md.GetEntityObject<T>(entity);
        e.NewRecord();
        init(e);
        if (!(await e.Save())) throw new OEPSaveError(OEPSaveMessage(e.LatestResult));
        return e;
    }

    async Update<T extends BaseEntity>(entity: OEPEntityName, id: string, mutate: (e: T) => void): Promise<T> {
        // GetEntityObject(name, key) loads the record (the v1 FP precedent, agu-data.service.ts:340).
        const e = await this.md.GetEntityObject<T>(entity, CompositeKey.FromID(id));
        if (!e.IsSaved) throw new OEPSaveError('That record is no longer available.');
        mutate(e);
        if (!(await e.Save())) throw new OEPSaveError(OEPSaveMessage(e.LatestResult));
        return e;
    }

    async Delete(entity: OEPWriteEntityName, id: string): Promise<void> {
        const e = await this.md.GetEntityObject<BaseEntity>(entity, CompositeKey.FromID(id));
        if (!e.IsSaved) return;
        if (!(await e.Delete())) throw new OEPSaveError(e.LatestResult ? OEPSaveMessage(e.LatestResult) : 'Delete failed');
    }
}
